import type { PublicPlans } from './api.ts';
import { T, type Lang, type Trilingual } from './i18n.ts';

/**
 * 价目表那张对比表的纯逻辑：显示哪几行、每一格怎么写。
 * 画表的组件在 components/PlanTable.tsx。
 *
 * 拆出来是为了能测 —— 测试文件不能引 `.tsx`（测试的类型检查不开 JSX）。
 */

/**
 * 表里显示哪几项、按什么顺序。其余的额度（导出次数、图标位…）对选档没有意义，不占位置。
 *
 * 这里列了、服务器上却没有的键，那一行不画 —— 不会凭空画出一行没有数字的东西。
 * 但那也意味着**键名拼错了这一行会静静消失**，所以 plans.test.ts 盯着：
 * 这里列的每个键，种子数据里都必须有。
 */
export const PLAN_ROWS: ReadonlyArray<{ key: string; label: Trilingual }> = [
  { key: 'wallets', label: T('同时盯几个钱包', '同時盯幾個錢包', 'Wallets watched') },
  { key: 'tokens', label: T('同时盯几个币的价格', '同時盯幾個幣的價格', 'Tokens on price watch') },
  { key: 'pricerules', label: T('价格告警规则', '價格告警規則', 'Price alert rules') },
  { key: 'price', label: T('价格最快多久查一次', '價格最快多久查一次', 'Fastest price check') },
  { key: 'channels', label: T('同时开几个提醒通道', '同時開幾個提醒通道', 'Alert channels at once') },
  { key: 'dailysend', label: T('每天最多发几条提醒', '每天最多發幾條提醒', 'Alerts per day') },
  { key: 'reminders', label: T('事项提醒条数', '事項提醒條數', 'Reminders') },
  { key: 'keepdays', label: T('历史记录保留', '歷史記錄保留', 'History kept') }
];

export function fmtSeconds(s: number, lang: Lang): string {
  if (s < 60) return lang === 'en' ? `${s}s` : `${s} 秒`;
  if (s < 3600) {
    const m = Math.round(s / 60);
    return lang === 'en' ? `${m} min` : lang === 'zh-HK' ? `${m} 分鐘` : `${m} 分钟`;
  }
  const h = Math.round(s / 360) / 10;
  return lang === 'en' ? `${h} h` : lang === 'zh-HK' ? `${h} 小時` : `${h} 小时`;
}

/** 一格怎么显示 */
export function formatQuota(
  q: PublicPlans['quotas'][number], tier: 'free' | 'vip', lang: Lang
): string {
  const v = tier === 'free' ? q.free : q.vip;
  if (q.kind === 'interval') return fmtSeconds(v, lang);
  if (q.key === 'keepdays') return lang === 'en' ? `${v} days` : `${v} 天`;
  /* 通道的硬上限就是「全部」：一共只有那几种通道，99 只是个占位的大数。
     显示成 99 会让人以为真有 99 个通道可选 */
  if (q.kind === 'count' && q.key === 'channels' && v >= q.hardCap) {
    return lang === 'en' ? 'All' : '全部';
  }
  return String(v);
}

/**
 * 年付比按月付一年省多少。
 *
 * **只在两个价格都是整数时算。** 价目表是运营填的，
 * 万一填了带小数的，这里宁可不显示，也不要显示一个浮点误差算出来的数。
 * 年付不比月付便宜时也不显示 —— 「省 -4 USDT」是在说胡话。
 */
export function yearlySaving(prices: PublicPlans['prices']): number | null {
  const y = Number(prices.yearUsd);
  const m = Number(prices.monthUsd);
  if (!Number.isInteger(y) || !Number.isInteger(m)) return null;
  return m * 12 > y ? m * 12 - y : null;
}
